import { create } from 'zustand';

/**
 * Admin display preferences. Currently just the timezone toggle — useful
 * when the operator is in one TZ but the business runs on Beijing time, so
 * they can flip between "what does the user actually see" and "what's it
 * for me right now".
 *
 * Persisted via localStorage so the choice survives page reloads.
 */

export type DisplayTz = 'Asia/Shanghai' | 'America/Vancouver';

const STORAGE_KEY = 'admin_display_tz';

function loadInitialTz(): DisplayTz {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'America/Vancouver' || v === 'Asia/Shanghai') return v;
  } catch {
    // localStorage unavailable (private mode etc) — fall through.
  }
  return 'Asia/Shanghai';
}

function persistTz(tz: DisplayTz): void {
  try {
    localStorage.setItem(STORAGE_KEY, tz);
  } catch {
    // best-effort
  }
}

interface DisplayState {
  tz: DisplayTz;
  toggleTz: () => void;
}

export const useDisplayStore = create<DisplayState>((set) => ({
  tz: loadInitialTz(),
  toggleTz: () =>
    set((s) => {
      const next: DisplayTz =
        s.tz === 'Asia/Shanghai' ? 'America/Vancouver' : 'Asia/Shanghai';
      persistTz(next);
      return { tz: next };
    }),
}));

/** Short label for the toggle button. */
export const TZ_LABEL: Record<DisplayTz, string> = {
  'Asia/Shanghai': '北京 UTC+8',
  'America/Vancouver': '温哥华 PT', // PT 随夏令时在 UTC-8(冬)/UTC-7(夏)间变, 别写死偏移(审计 L4)
};
